import { Box, Em, Flex, Grid, Text } from '@workos-inc/design-system';
import * as React from 'react';
interface DiagramRootProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}
const DiagramRoot = ({
  children,
  style
}: DiagramRootProps) => <Box aria-hidden style={{
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--gray-2)',
  backgroundImage: 'radial-gradient(var(--gray-a6) 6%, transparent 10%)',
  backgroundPosition: '8px 8px',
  backgroundSize: '16px 16px',
  fontSize: 'var(--font-size-2)',
  lineHeight: 18 / 14,
  overflow: 'hidden',
  padding: 'var(--space-6)',
  pointerEvents: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
  ...style
}} data-sentry-element="Box" data-sentry-component="DiagramRoot" data-sentry-source-file="diagram.tsx">
    {children}
  </Box>;
interface Size {
  width: React.CSSProperties['width'];
  height: React.CSSProperties['height'];
}
const DiagramPanel = ({
  children,
  width,
  height
}: React.PropsWithChildren & Size) => <Grid style={{
  gridTemplateRows: 'auto 1fr',
  gridAutoRows: '0',
  background: 'var(--color-panel-solid)',
  borderRadius: 'var(--radius-5)',
  boxShadow: '0 0 0.5px 1px var(--gray-6), var(--shadow-4)',
  overflow: 'hidden',
  width,
  height
}} data-sentry-element="Grid" data-sentry-component="DiagramPanel" data-sentry-source-file="diagram.tsx">
    {children}
  </Grid>;
const DiagramPanelHeader = ({
  children
}: React.PropsWithChildren) => <Flex align="center" height="32px" justify="center" style={{
  background: 'var(--gray-a3)'
}} data-sentry-element="Flex" data-sentry-component="DiagramPanelHeader" data-sentry-source-file="diagram.tsx">
    <Text size="2" weight="bold" data-sentry-element="Text" data-sentry-source-file="diagram.tsx">
      {children}
    </Text>
  </Flex>;
const DiagramPanelContent = ({
  children
}: React.PropsWithChildren) => <Box minHeight="0" data-sentry-element="Box" data-sentry-component="DiagramPanelContent" data-sentry-source-file="diagram.tsx">{children}</Box>;
const DiagramPanelFade = () => <Box style={{
  height: 120,
  background: 'linear-gradient(to bottom, transparent, var(--color-panel-solid))',
  marginTop: -100
}} data-sentry-element="Box" data-sentry-component="DiagramPanelFade" data-sentry-source-file="diagram.tsx" />;
const DiagramCaption = ({
  children
}: React.PropsWithChildren) => <Text align="center" style={{
  fontSize: 15,
  lineHeight: '20px'
}} data-sentry-element="Text" data-sentry-component="DiagramCaption" data-sentry-source-file="diagram.tsx">
    <Em data-sentry-element="Em" data-sentry-source-file="diagram.tsx">{children}</Em>
  </Text>;
const DiagramYourAppIcon = (props: React.ComponentPropsWithoutRef<'svg'>) => <svg fill="none" height="128" stroke="var(--blue-a9)" viewBox="0 0 128 128" width="128" xmlns="http://www.w3.org/2000/svg" {...props} data-sentry-element="svg" data-sentry-component="DiagramYourAppIcon" data-sentry-source-file="diagram.tsx">
    <path d="M32 0v128M96 0v128" strokeDasharray="4 2" data-sentry-element="path" data-sentry-source-file="diagram.tsx" />
    <path d="M0 127.28 127.28 0M64 0v128M0 64h128M127.28 127.28 0 0" data-sentry-element="path" data-sentry-source-file="diagram.tsx" />
    <path d="M0 32h128M0 96h128" strokeDasharray="4 2" data-sentry-element="path" data-sentry-source-file="diagram.tsx" />
    <circle cx="64" cy="64" r="26.93" data-sentry-element="circle" data-sentry-source-file="diagram.tsx" />
    <circle cx="64" cy="64" r="23.69" data-sentry-element="circle" data-sentry-source-file="diagram.tsx" />
    <circle cx="64" cy="64" r="15.66" data-sentry-element="circle" data-sentry-source-file="diagram.tsx" />
    <circle cx="64" cy="64" r="12.87" data-sentry-element="circle" data-sentry-source-file="diagram.tsx" />
    <rect height="64" rx="13" width="64" x="32" y="32" data-sentry-element="rect" data-sentry-source-file="diagram.tsx" />
  </svg>;
interface DiagramArrowProps {
  direction?: 'left' | 'right' | 'up' | 'down';
}
const DiagramArrow = ({
  direction = 'right'
}: DiagramArrowProps) => <Flex align="center" height="100%" justify="center" width="100%" direction={direction === 'right' && 'row' || direction === 'left' && 'row-reverse' || direction === 'down' && 'column' || direction === 'up' && 'column-reverse' || undefined} data-sentry-element="Flex" data-sentry-component="DiagramArrow" data-sentry-source-file="diagram.tsx">
    {/* Space before the shaft */}
    <Box flexShrink="0" height="6px" width="6px" data-sentry-element="Box" data-sentry-source-file="diagram.tsx" />

    {/* Arrow shaft */}
    <Box style={{
    background: 'var(--gray-9)',
    borderRadius: 100,
    flexGrow: 1,
    height: direction === 'right' || direction === 'left' ? 2 : '100%',
    width: direction === 'up' || direction === 'down' ? 2 : '100%',
    marginLeft: direction === 'left' ? -2 : 0,
    marginRight: direction === 'right' ? -2 : 0,
    marginTop: direction === 'up' ? -2 : 0,
    marginBottom: direction === 'down' ? -2 : 0
  }} data-sentry-element="Box" data-sentry-source-file="diagram.tsx" />

    {/* Arrow tip */}
    <Box flexShrink="0" style={{
    borderStyle: 'solid',
    borderColor: 'transparent',
    ...(direction === 'left' && {
      borderWidth: '4px 7px 4px 0px',
      borderRightColor: 'var(--gray-9)'
    }),
    ...(direction === 'right' && {
      borderWidth: '4px 0px 4px 7px',
      borderLeftColor: 'var(--gray-9)'
    }),
    ...(direction === 'up' && {
      borderWidth: '0px 4px 7px',
      borderBottomColor: 'var(--gray-9)'
    }),
    ...(direction === 'down' && {
      borderWidth: '7px 4px 0px',
      borderTopColor: 'var(--gray-9)'
    })
  }} data-sentry-element="Box" data-sentry-source-file="diagram.tsx" />

    {/* Space after the tip  */}
    <Box flexShrink="0" height="5px" width="5px" data-sentry-element="Box" data-sentry-source-file="diagram.tsx" />
  </Flex>;
export const Diagram = {
  Root: DiagramRoot,
  Panel: DiagramPanel,
  PanelHeader: DiagramPanelHeader,
  PanelContent: DiagramPanelContent,
  PanelFade: DiagramPanelFade,
  Arrow: DiagramArrow,
  Caption: DiagramCaption,
  YourAppIcon: DiagramYourAppIcon
};